import { useState, useEffect, useRef } from 'react';
import { Download, X, WifiOff, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

export default function PWAInstallPrompt() {
  const [installPrompt, setInstallPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(() => {
    // Initialize based on localStorage
    const dismissed = localStorage.getItem('pwaPromptDismissed');
    if (dismissed) {
      const dayAgo = Date.now() - 24 * 60 * 60 * 1000;
      return parseInt(dismissed) <= dayAgo;
    }
    return false;
  });
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [hasUpdate, setHasUpdate] = useState(false);
  const [updateRegistration, setUpdateRegistration] = useState(null);

  useEffect(() => {
    // Capture the install prompt
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
      
      // Show prompt after a delay if not installed
      const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
      if (!isStandalone) {
        setTimeout(() => setShowPrompt(true), 3000);
      }
    };

    // Listen for online/offline
    const handleOnline = () => {
      setIsOnline(true);
      toast.success('Conexión restaurada', { duration: 2000 });
    };
    
    const handleOffline = () => {
      setIsOnline(false);
      toast.warning('Sin conexión - Modo offline activado', { duration: 3000 });
    };

    // Listen for SW updates
    const handleSwUpdate = (e) => {
      setHasUpdate(true);
      setUpdateRegistration(e.detail.registration);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    window.addEventListener('swUpdate', handleSwUpdate);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('swUpdate', handleSwUpdate);
    };
  }, []);

  const handleInstall = async () => {
    if (!installPrompt) return;

    installPrompt.prompt();
    const { outcome } = await installPrompt.userChoice;
    
    if (outcome === 'accepted') {
      toast.success('¡App instalada exitosamente!');
    }
    
    setInstallPrompt(null);
    setShowPrompt(false);
  };

  const handleUpdate = () => {
    if (updateRegistration && updateRegistration.waiting) {
      updateRegistration.waiting.postMessage({ type: 'SKIP_WAITING' });
      window.location.reload();
    }
  };

  const dismissPrompt = () => {
    setShowPrompt(false);
    // Don't show again for 24 hours
    localStorage.setItem('pwaPromptDismissed', Date.now().toString());
  };

  // No need for useEffect to check dismissed - it's handled in useState initializer

  return (
    <>
      {/* Offline indicator */}
      {!isOnline && (
        <div className="fixed top-0 left-0 right-0 bg-amber-500 text-white text-center py-1 text-sm z-50 flex items-center justify-center gap-2">
          <WifiOff className="w-4 h-4" />
          <span>Modo Offline - Algunos datos pueden estar desactualizados</span>
        </div>
      )}

      {/* Update available banner */}
      {hasUpdate && (
        <div className="fixed bottom-20 left-4 right-4 sm:left-auto sm:right-4 sm:w-80 bg-blue-600 text-white rounded-lg shadow-lg p-4 z-50 animate-in slide-in-from-bottom">
          <div className="flex items-start gap-3">
            <RefreshCw className="w-5 h-5 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="font-semibold">Actualización disponible</p>
              <p className="text-sm opacity-90">Hay una nueva versión de la app</p>
            </div>
          </div>
          <div className="flex gap-2 mt-3">
            <button
              onClick={handleUpdate}
              className="flex-1 bg-white text-blue-600 font-medium py-2 px-3 rounded-lg hover:bg-blue-50 transition-colors"
            >
              Actualizar ahora
            </button>
            <button
              onClick={() => setHasUpdate(false)}
              className="px-3 py-2 text-white/80 hover:text-white transition-colors"
            >
              Después
            </button>
          </div>
        </div>
      )}

      {/* Install prompt */}
      {showPrompt && installPrompt && (
        <div className="fixed bottom-20 left-4 right-4 sm:left-auto sm:right-4 sm:w-80 bg-white border border-slate-200 rounded-xl shadow-xl p-4 z-50 animate-in slide-in-from-bottom">
          <button
            onClick={dismissPrompt}
            className="absolute top-2 right-2 text-slate-400 hover:text-slate-600"
          >
            <X className="w-5 h-5" />
          </button>
          
          <div className="flex items-start gap-3 mb-4">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-blue-800 rounded-xl flex items-center justify-center">
              <span className="text-white font-bold text-lg">E</span>
            </div>
            <div>
              <h3 className="font-bold text-slate-900">Instalar EDMUNDO</h3>
              <p className="text-sm text-slate-500">Acceso rápido y modo offline</p>
            </div>
          </div>

          <ul className="text-sm text-slate-600 space-y-1 mb-4">
            <li className="flex items-center gap-2">
              <span className="text-green-500">✓</span> Acceso desde tu pantalla
            </li>
            <li className="flex items-center gap-2">
              <span className="text-green-500">✓</span> Funciona sin internet
            </li>
            <li className="flex items-center gap-2">
              <span className="text-green-500">✓</span> Notificaciones push
            </li>
          </ul>

          <button
            onClick={handleInstall}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-4 rounded-lg transition-colors flex items-center justify-center gap-2"
          >
            <Download className="w-5 h-5" />
            Instalar App
          </button>
        </div>
      )}
    </>
  );
}
